import * as Sentry from '@sentry/react';
import type { InviteErrorCode } from '@karehero/models';

import { dispatch } from 'store';
import { newToast, type ToastQueueItem } from 'store/slices/uiSlice';

import { apiErrorMessage, apiErrorStatus, isInputError } from './apiErrors';

const TITLES: Record<InviteErrorCode, string> = {
  // Worded like a send failure so the toast does not reveal that the address has an account.
  'already-registered': 'Sorry, something went wrong',
  'invalid-email': 'Check the email address',
  'send-failed': 'Sorry, something went wrong',
  unknown: 'Sorry, something went wrong',
};

const CHECK_THE_ADDRESS = 'Please check the email address and try again.';
const TRY_AGAIN = 'Please try again.';

/** The toast for a failed invite, resend or removal: a title per reason, with the API's message underneath. */
export const inviteErrorToast = (error: unknown): ToastQueueItem => {
  const code = (error as { data?: { code?: unknown } } | undefined)?.data?.code;

  if (typeof code === 'string' && code in TITLES) {
    return {
      title: TITLES[code as InviteErrorCode],
      description: apiErrorMessage(error, TRY_AGAIN),
      variant: 'error',
    };
  }

  // A 400 without a code is the API refusing the request body before the handler ran. The email
  // is the only field it format-checks, so that is what to correct.
  const fallback =
    apiErrorStatus(error) === 400 ? CHECK_THE_ADDRESS : TRY_AGAIN;

  return {
    title: TITLES.unknown,
    description: apiErrorMessage(error, fallback),
    variant: 'error',
  };
};

/** Shows the toast for a failed invite, resend or removal, and reports anything other than a refused request to Sentry. */
export const reportInviteError = (error: unknown): void => {
  if (!isInputError(error)) Sentry.captureException(error);
  dispatch(newToast(inviteErrorToast(error)));
};
